/**
 * GameBoard
 *
 * The full game view: header bar, hearts, tile grid, action bar,
 * game-over message, and the CATCH button.
 * Extracted from App.js lines 909–1177.
 *
 * Props:
 *   gridWidth      {number}   - Number of tile columns.
 *   gridHeight     {number}   - Number of tile rows.
 *   maxLives       {number}   - Maximum hearts.
 *   lives          {number}   - Current hearts remaining.
 *   stats          {object}   - { currentStreak } used in the header.
 *   level          {number}   - Player level shown in the header.
 *   timeLeft       {string}   - Countdown string shown in the header.
 *   cleared        {string[]} - Array of cleared tile IDs.
 *   flags          {string[]} - Array of flagged tile IDs.
 *   fishPos        {object}   - { x, y } position of the fish tile.
 *   obstacles      {string[]} - Array of snag tile IDs.
 *   gameState      {string}   - 'playing' | 'won' | 'lost'.
 *   isFlaggingMode {boolean}  - Whether flag mode is active.
 *   showHelp       {boolean}  - Whether the how-to-play overlay is visible.
 *   hasCaughtToday {boolean}  - Whether the catch button has been pressed.
 *   pulsingTiles   {string[]} - Tile IDs currently playing the sonar pulse.
 *   activeSnagTile {string}   - Tile ID currently playing the snag animation.
 *   getIsSelectable{function} - (x, y) => boolean.
 *   onTileClick    {function} - (x, y) => void.
 *   onToggleFlag   {function} - Toggles flagging mode.
 *   onShowHelp     {function} - Opens the how-to-play overlay.
 *   onCloseHelp    {function} - Closes the how-to-play overlay.
 *   onCatch        {function} - Called when CATCH button is pressed.
 *   onBack         {function} - Navigates back to the menu.
 */

import HowToPlayOverlay from './HowToPlayOverlay';
import { tileUnselected, tileAvailable, tileSnag, tileFish, flagDecal, SONAR_IMAGES } from '../constants/assets';

export default function GameBoard({
  gridWidth,
  gridHeight,
  maxLives,
  lives,
  stats,
  level,
  timeLeft,
  cleared,
  flags,
  fishPos,
  obstacles,
  gameState,
  isFlaggingMode,
  showHelp,
  hasCaughtToday,
  pulsingTiles,
  activeSnagTile,
  getIsSelectable,
  onTileClick,
  onToggleFlag,
  onShowHelp,
  onCloseHelp,
  onCatch,
  onBack,
}) {
  return (
    <div style={{ textAlign: 'center', width: '100%', maxWidth: '400px', zIndex: 1 }}>

      {/* TOP NAV BUTTONS */}
      <button
        onClick={onBack}
        style={{ position: 'fixed', top: 15, left: 15, zIndex: 999, padding: '10px', background: '#30363d', color: 'white', border: '1px solid #8b949e', borderRadius: '4px', fontSize: '10px', fontFamily: 'inherit', cursor: 'pointer' }}
      >
        ←
      </button>

      {window.location.hostname === 'localhost' && (
        <button
          onClick={() => { localStorage.clear(); window.location.reload(); }}
          style={{ position: 'fixed', top: 15, right: 15, zIndex: 999, padding: '10px', background: '#ff4d4d', color: 'white', border: '2px solid white', borderRadius: '4px', fontSize: '8px', fontFamily: 'inherit', cursor: 'pointer' }}
        >
          DEBUG
        </button>
      )}

      {/* HEADER: Streak, Timer, Level */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '15px', width: '100%' }}>
        
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', backgroundColor: '#161b22', padding: '6px 12px', borderRadius: '8px', border: '1px solid #30363d', justifyContent: 'center' }}>
          
          {/* Streak */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '14px' }}>🔥</span>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#f1c40f', fontFamily: "'Press Start 2P', cursive" }}>
              {stats.currentStreak}
            </span>
          </div>

          <div style={{ width: '1px', height: '14px', backgroundColor: '#30363d' }} />

          {/* Timer */}
          <div style={{ color: '#8b949e', fontSize: '8px', fontFamily: "'Press Start 2P', cursive", whiteSpace: 'nowrap' }}>
            NEXT: {timeLeft}
          </div>

          <div style={{ width: '1px', height: '14px', backgroundColor: '#30363d' }} />

          {/* Level */}
          <div style={{ color: '#2ecc71', fontSize: '8px', fontFamily: "'Press Start 2P', cursive", whiteSpace: 'nowrap' }}>
            LVL {level}
          </div>
        </div>

        {/* Hearts */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20px' }}>
          {Array.from({ length: maxLives }).map((_, i) => (
            <span
              key={i}
              style={{
                fontSize: '20px',
                color: i < lives ? '#ff4d4d' : '#30363d',
                margin: '0 3px',
                filter: i < lives ? 'drop-shadow(0 0 5px rgba(255, 77, 77, 0.3))' : 'none',
              }}
            >
              {i < lives ? '❤️' : '🖤'}
            </span>
          ))}
        </div>
      </div>

      {/* TILE GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${gridWidth}, 1fr)`, gap: '8px', position: 'relative', width: 'min(95vw, 350px)', margin: '0 auto' }}>
        {[...Array(gridHeight)].map((_, y) =>
          [...Array(gridWidth)].map((_, x) => {
            const id = `${x}-${y}`;
            const isCleared  = cleared.includes(id);
            const isObs      = obstacles.includes(id);
            const isFish     = x === fishPos.x && y === fishPos.y;
            const hasFlag    = flags.includes(id);
            const isAvailable = getIsSelectable(x, y);
            const isPulsing  = pulsingTiles.includes(id);
            const isSnagging = activeSnagTile === id;

            let currentTileImage = tileUnselected;
            if (isCleared) {
              if (isObs) {
                currentTileImage = tileSnag;
              } else if (isFish) {
                currentTileImage = tileFish;
              } else {
                let count = 0;
                for (let dx = -1; dx <= 1; dx++) {
                  for (let dy = -1; dy <= 1; dy++) {
                    if (dx === 0 && dy === 0) continue;
                    if (obstacles.includes(`${x + dx}-${y + dy}`)) count++;
                  }
                }
                currentTileImage = SONAR_IMAGES[count];
              }
            } else if (isAvailable) {
              currentTileImage = tileAvailable;
            }

            return (
              <button
                key={id}
                onClick={() => onTileClick(x, y)}
                className={`${isPulsing ? 'pulse-active' : ''} ${isSnagging ? 'snag-active' : ''}`}
                style={{
                  aspectRatio: '1/1', width: '100%', padding: '0',
                  borderRadius: '6px', border: 'none',
                  backgroundColor: 'transparent',
                  backgroundImage: `url(${currentTileImage})`,
                  backgroundSize: 'cover', imageRendering: 'pixelated',
                  cursor: (isAvailable && gameState === 'playing') ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative', transition: 'transform 0.1s ease',
                }}
              >
                {hasFlag && !isCleared && (
                  <img src={flagDecal} alt="flag" style={{ width: '80%', height: '80%', imageRendering: 'pixelated' }} />
                )}
              </button>
            );
          })
        )}

        {showHelp && <HowToPlayOverlay onClose={onCloseHelp} />}
      </div>

      {/* ACTION BAR: Flag + Help */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', width: '100%', maxWidth: '350px', height: '40px' }}>
          
          <button
            onClick={onToggleFlag}
            style={{
              flex: '0 0 70%', cursor: 'pointer',
              backgroundColor: isFlaggingMode ? '#f1c40f' : '#30363d',
              border: 'none', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <img
              src={flagDecal}
              alt="flag"
              style={{ width: '24px', height: '24px', imageRendering: 'pixelated', filter: isFlaggingMode ? 'brightness(0.2)' : 'none' }}
            />
            <span style={{ marginLeft: '10px', fontWeight: 'bold', color: isFlaggingMode ? '#000' : '#fff', fontSize: '14px', fontFamily: "'Press Start 2P', cursive" }}>
              {isFlaggingMode ? 'FLAGGING' : 'FLAG'}
            </span>
          </button>

          <button
            onClick={onShowHelp}
            style={{ flex: '1', fontSize: '18px', cursor: 'pointer', backgroundColor: '#1f6feb', color: 'white', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontWeight: 'bold' }}
          >
            ?
          </button>
        </div>
      </div>

      {/* GAME OVER MESSAGE */}
      {lives <= 0 && (
        <div
          className="snag-active"
          style={{ marginTop: '25px', color: '#ff4d4d', fontSize: '25px', fontWeight: 'bold', fontFamily: "'Press Start 2P', cursive", textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}
        >
          LINE SNAPPED
        </div>
      )}

      {/* CATCH BUTTON */}
      {gameState === 'won' && !hasCaughtToday && (
        <button
          onClick={onCatch}
          style={{
            marginTop: '20px', padding: '15px 10px',
            backgroundColor: '#f1c40f', color: '#000',
            border: '4px solid #fff', borderRadius: '8px',
            fontSize: '20px', fontFamily: 'inherit', cursor: 'pointer',
            boxShadow: '0 0 20px rgba(241, 196, 15, 0.6)',
            opacity: hasCaughtToday ? 0.5 : 1,
            pointerEvents: hasCaughtToday ? 'none' : 'auto',
          }}
        >
          {hasCaughtToday ? 'REELING...' : 'CATCH!'}
        </button>
      )}

    </div>
  );
}